/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 24-07-2026
 *
 * Component voor de normbalk onderaan het modelcanvas (tot golf 4f een Custom Element): per elementtype
 * dat het normmodel verwacht het bijbehorende icoon met "geteld / verwacht", en bij een normmatch ook
 * het aantal fouten. Vervangt `#onderbalk`/`#normmodel` in canvas.html.
 *
 * Het normdocument en de telling komen uit `DlModel`; de `NormService` rekent de telling uit en zet hem
 * daar neer (zie de `normtelling`-setter in `dl-model.ts`).
 */
import {ChangeDetectionStrategy, Component, Input, computed, inject, signal} from '@angular/core';
import {BaseIslandComponent} from '../base-island.component';
import {DlModel} from '../../app/model/dl-model';
import {VideoStatusService} from '../../services/video-status.service';

/**
 * Eén regel van de balk: het elementtype met het verwachte en het getelde aantal, en het aantal
 * fouten uit de normmatch (`null` als er geen normmatch is).
 */
interface NormbalkRegel {
    type: string;
    verwacht: number;
    geteld: number;
    fouten: number | null;
}

/**
 * De modelleerniveaus waarop de losse invloed- en proportionaliteitsregels vervallen ten gunste van de
 * gecombineerde causal-regel, en omgekeerd. Overgenomen uit de `[modelleerniveau]`-attribuutselectors
 * in canvas.less.
 */
const NIVEAUS_ZONDER_LOSSE_CAUSALEN = [20, 25, 30];
const NIVEAUS_ZONDER_GECOMBINEERDE_CAUSAAL = [40, 50];

/**
 * De elementtypen die alleen op de lagere niveaus getoond worden, en het type dat juist alleen op de
 * hogere niveaus meedoet.
 */
const LOSSE_CAUSALEN = ['proportionality', 'influence'];
const GECOMBINEERDE_CAUSAAL = 'causal';

@Component({
    selector: 'dl-normbalk',
    standalone: true,
    templateUrl: './dl-normbalk.component.html',
    styleUrls: ['./dl-normbalk.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.dl-normbalk--zichtbaar]': 'zichtbaar()',
        '[class.dl-normbalk--onbekend]': 'onbekend()'
    }
})
export class DlNormbalkComponent extends BaseIslandComponent {

    /**
     * Het model: levert het normdocument (verwachtingen) en de telling.
     */
    private readonly dlModel = inject(DlModel);

    /**
     * De gedeelde videostatus: de balk wijkt voor een spelende instructievideo.
     */
    private readonly videoStatus = inject(VideoStatusService);

    constructor() {
        super();
    }

    // --- Inputs ---

    /**
     * Of het project de normbalk toont (projectvlag `norm_balk`).
     */
    @Input()
    set toon(waarde: boolean) {
        this._toon.set(waarde);
        this.cdr.markForCheck();
    }
    protected readonly _toon = signal(false);

    // --- Afgeleide state ---

    /**
     * De balk staat er alleen als het project hem toont, er een normmodel is, en er geen
     * instructievideo speelt.
     */
    protected readonly zichtbaar = computed(() => this._toon() && !!this.dlModel.norm && !this.videoStatus.speelt());

    /**
     * De regels van de balk, in de volgorde van de telling — en die volgt de tellers van het
     * normmodel. Bewust uit de telling en niet uit `norm.teller`: een replaystap draagt zijn eigen
     * telling mét de verwachting van tóen, en zou anders tegen de norm van nu worden afgebeeld. Voor
     * een telling zonder verwachting (van een oudere leider) is `norm.teller` de terugval.
     *
     * Een regel doet mee zodra het normmodel er iets van verwacht óf het model er iets van heeft.
     */
    protected readonly regels = computed<NormbalkRegel[]>(() => {
        const norm = this.dlModel.norm;
        if (!norm) {
            return [];
        }
        const telling = this.dlModel.normtelling?.telling ?? {};
        //bij een onbekende stand blijft de vórm van de balk staan — de typen uit de normdefinitie —
        //maar zonder cijfers; die zouden bij een andere stand horen. Zie `onbekend`
        return (this.onbekend() ? Object.keys(norm.teller) : Object.keys(telling))
            .map(type => ({
                type,
                verwacht: telling[type]?.verwacht ?? norm.teller[type] ?? 0,
                geteld: telling[type]?.aantal || 0,
                fouten: telling[type]?.fouten ?? null
            }))
            .filter(regel => (regel.verwacht || regel.geteld) && this.hoortBijNiveau(regel.type));
    });

    /**
     * De namentelling, of `null` als het normmodel geen namen verwacht.
     */
    protected readonly namen = computed(() => {
        if (this.onbekend()) {
            //ook hier blijft de vorm staan als het normmodel namen verwacht; de cijfers niet
            const norm = this.dlModel.norm;
            return norm && Object.keys(norm.namen ?? {}).length ? {norm: 0, aantal: 0} : null;
        }
        const naamtelling = this.dlModel.normtelling?.naamtelling;
        return naamtelling?.norm ? naamtelling : null;
    });

    /**
     * Of de foutaantallen getoond worden; alleen zinvol met een normmatch.
     */
    protected readonly toonFouten = computed(() => !!this.dlModel.normtelling?.heeftMatch);

    /**
     * Of er over deze stand niets bekend is: een replaystap zonder normblok. De balk staat er dan
     * wel, maar gedimd en leeg — de cijfers van nu horen bij een andere stand, en niets tonen zou
     * suggereren dat het model foutloos is.
     */
    protected readonly onbekend = computed(() => !!this.dlModel.normtelling?.onbekend);

    // --- Hulpmethodes voor de template ---

    /**
     * De kleurklasse van een telling: groen bij precies goed, rood bij te veel, en geen klasse zolang
     * er nog te weinig zijn.
     */
    protected kleurVoor(geteld: number, verwacht: number): string {
        switch (true) {
            case geteld === verwacht:
                return 'dl-normbalk__telling--goed';
            case geteld > verwacht:
                return 'dl-normbalk__telling--veel';
            default:
                return '';
        }
    }

    /**
     * Of een elementtype op het huidige modelleerniveau meedoet. De causale relaties worden op de
     * lagere niveaus als één `causal`-regel getoond en daarboven juist gesplitst in invloed en
     * proportionaliteit.
     */
    private hoortBijNiveau(type: string): boolean {
        const niveau = this.dlModel.effectief_modelleerniveau;
        switch (true) {
            case LOSSE_CAUSALEN.includes(type):
                return !NIVEAUS_ZONDER_LOSSE_CAUSALEN.includes(niveau);
            case type === GECOMBINEERDE_CAUSAAL:
                return !NIVEAUS_ZONDER_GECOMBINEERDE_CAUSAAL.includes(niveau);
            default:
                return true;
        }
    }
}
